// ============================================================================
// SOP detail drawer — Step 3 (2026-05-28) of cowork-new-account-sop-kanban-build.
//
// Six tabs replace the prior Phase-1 scroll layout:
//   1. Info             — account/card/facility_type/market/start_date/status
//   2. Pricing & Billing — pricing + payment terms + service days + billing + address
//   3. Contractor        — is_new_subcontractor toggle + contractor name/phone/email
//   4. Checklist         — 7-stage JSONB, check/uncheck writes back; auto-promote prompt
//   5. Attachments       — read-only Trello carry-over links (storage uploads later)
//   6. Activity          — audit_log entries for this sop_id, newest-first
//
// Info tab also has a "Generate summary" action (AFB-0023 #5) → copyable
// Antonela→Amy billing-setup email. Footer actions: Move to stage / Mark
// stalled / Cancel card / Delete (soft-delete, owner-or-admin, AFB-0023 #6).
//
// Mobile: bottom-sheet pattern with a single tab visible at a time and a top
// chip bar to switch. Desktop: right-side panel ~520px wide.
// ============================================================================

import { canArchiveNewAccountCard, hasFullAmAuthority } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Loader2, Trash2, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { fetchAmNameMap } from '../../shared/am-name';
import { useAuth } from '../../shared/auth';
// ⛔ The sub picker, the create-a-sub panel and the sub-mode helpers are no
// longer imported here: this screen stopped having a second subcontractor
// editor on 2026-09-14. They all still serve the ONE editor, per line, in
// service-line-card.tsx.
import { serviceLinesFromRow } from './lib/service-lines';
import { ServiceLinesEditor } from './service-lines-editor';
import {
  NEW_ACCOUNT_SOP_STAGES,
  NEW_ACCOUNT_SOP_STAGE_LABELS,
  NAS_FACILITY_TYPE_LABELS,
  NAS_FACILITY_TYPE_OPTIONS,
  type NewAccountSop,
  type NewAccountSopActiveStage,
  type NewAccountSopFacilityType,
  type NewAccountSopStage,
} from '../../shared/types';
import { useMarkets, marketOptions, marketColorPair } from '../../shared/markets';
import { archiveSopCard, deleteBuildingWords, previewSopDelete, recordSopSignoff, type SopPatch, type SopSignoffKind } from './hooks/use-sop-data';
import { AccountStartPanel } from './account-start-panel';
import { StartupChecklist, isStartupArchived } from './startup-checklist';
import { overlayClickClose } from '../../shared/overlay-close';
import { fmtBareDate, localToday } from '../../shared/format';
import { errorMessage } from '../../shared/errors';
import {
  USABLES_OPTIONS,
  fromUsablesChoice,
  toUsablesChoice,
  usablesLabel,
} from '../../shared/facility-billing';
import { feePctToFraction, fractionToFeePct } from './mgmt-fee';
import {
  HOURLY_CARD_PRICE_HINT,
  HOURLY_ECONOMICS_CAPTION,
  hourlyBilledText,
  hourlyCardRates,
  hourlyEconomics,
  perHour,
  rateBasisChip,
} from '../../shared/rate-basis';
import { blockedStageMoveReason } from './intake-answers';
import {
  CHECKLIST_STAGE_TO_DB_STAGE,
  STAGE_PROGRESSION,
  stageMoveCheck,
  stagesForDbStage,
  type ChecklistJson,
} from './lib/stage-move';
import { useTaskActorNames } from '../tasks/hooks/use-task-form-options';
import {
  cardDisplayName,
  resolveCardName,
  SINGLE_SITE_CUSTOMER_LABEL,
} from './card-name';

interface Props {
  row: NewAccountSop;
  onClose: () => void;
  onPatch: (sopId: string, patch: SopPatch) => Promise<void>;
  // Delete (AFB-0023 #6) — routes through the delete_new_account_sop RPC
  // (the deletion_requests workflow, mirroring delete_project). Optional so
  // callers that don't support delete can omit it; the Footer renders the
  // affordance only when this is provided AND the viewer is the card owner or
  // an admin. Receives the required deletion reason.
  onDelete?: (sopId: string, reason: string) => Promise<void>;
  // Refetch the board after a start-confirmation / start-date edit (PR-4a).
  onRefetch?: () => void;
}

type TabKey =
  | 'info'
  | 'pricing'
  | 'contractor'
  | 'checklist'
  | 'attachments'
  | 'activity';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'info', label: 'Info' },
  { key: 'pricing', label: 'Pricing & Billing' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'checklist', label: 'Checklist' },
  { key: 'attachments', label: 'Documents' },
  { key: 'activity', label: 'Activity' },
];

const CHECKLIST_STAGE_LABEL: Record<string, string> = {
  stage_1_bdd: '1. BDD',
  stage_2_am_sub_checklist: '2. AM + Sub Checklist',
  // ⛔ The stored group KEY still names a person; renaming it is a data migration
  // on 39 live cards and is Ben's decision 33. The LABEL a user reads does not
  // have to wait for that, and does not (TCSD-0074).
  stage_3_antonela_billing: '3. Billing',
  stage_4_centralized_call: '4. Centralized Call',
  stage_5_exec_review: '5. Executive Review',
  stage_6_qbo_setup: '6. QBO Setup',
  stage_7_final_check: '7. Final Check',
};


/**
 * Why an ARCHIVED card is read-only, said on the card rather than left for the
 * person to discover by typing.
 *
 * ⛔ Archived, NOT completed (Ben, 2026-09-11). Finishing the checklist is the
 * middle of onboarding, not the end of it — admin routinely finishes after the
 * building goes live. Finance archives the card afterwards, and that is the
 * close. An earlier version of this banner appeared at Completed and froze the
 * form while people were still working in it.
 *
 * ⛔ It names the building, because "you cannot change this here" is only half
 * an answer — the other half is where they CAN change it, and the building's
 * own card is open for business as usual.
 */
function ClosedBanner({ row }: { row: NewAccountSop }) {
  // One id, one lookup, through the shared resolver — a name is never derived
  // from an eid by hand anywhere in this app.
  const [closedByName, setClosedByName] = useState<string | null>(null);
  const closedBy = row.archived_by_eid ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!closedBy) {
      setClosedByName(null);
      return;
    }
    void fetchAmNameMap([closedBy]).then((m) => {
      if (!cancelled) setClosedByName(m.get(closedBy) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [closedBy]);
  const cancelled =
    row.stage === 'Cancelled' || row.status === 'Cancelled';
  const when = row.archived_at
    ? new Date(row.archived_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;
  return (
    <div className="mx-4 mt-2 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
      <span className="font-medium text-tcs-text">
        {cancelled
          ? 'This onboarding card was cancelled'
          : 'This onboarding card is archived'}
      </span>
      {when ? ` — ${when}` : ''}
      {closedByName ? `, by ${closedByName}` : ''}. Its checklist and its fields
      are a record of what was done, and nothing here changes anything now.
      {row.facility_id && (
        <>
          {' '}
          The building carries on as normal —{' '}
          <Link
            to={`/account-management/facilities/${row.facility_id}`}
            className="font-medium text-tcs-text underline"
          >
            open {row.facility_name ?? row.facility_id}
          </Link>{' '}
          to make a change.
        </>
      )}
    </div>
  );
}

export default function SopDetail({ row, onClose, onPatch, onDelete, onRefetch }: Props) {
  const { employee, profile } = useAuth();
  const [tab, setTab] = useState<TabKey>('info');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);

  // ⛔ A CLOSED CARD FREEZES ITS CONTENT, NOT ITS LIFECYCLE.
  //
  // The details and the checklist stop changing — that is the point of closing
  // it, and the form says so rather than letting somebody type a correction the
  // database will refuse. But the controls that act ON the closed state —
  // moving the stage, cancelling, archiving, deleting — must stay live. My
  // first version disabled those too, which locked the door from the inside:
  // a card completed by mistake could not be moved back, because the control
  // that would move it was disabled by the state it would clear.
  //
  // `card_is_open` is undefined only on a row that predates the window view,
  // and an unknown must never lock a card nobody can then fix.
  const cardClosed = row.card_is_open === false;
  /** Content: fields, checklist, attachments. Frozen once the card closes. */
  const locked = updating || cardClosed;
  /** Lifecycle: stage, cancel, archive, delete. Only ever waits on a save. */
  const lifecycleBusy = updating;

  // #6 delete gate: the card's own creator, or a full-authority admin
  // (superuser / executive / national_am — the project-wide convention).
  const canDelete = useMemo(() => {
    const role = profile?.role ?? null;
    const isAdmin = hasFullAmAuthority(role);
    const myEid = employee?.employee_id ?? null;
    return isAdmin || (!!row.created_by_eid && row.created_by_eid === myEid);
  }, [profile?.role, employee?.employee_id, row.created_by_eid]);

  const checklist = useMemo<ChecklistJson>(() => {
    if (!row.checklist || typeof row.checklist !== 'object') return {};
    return row.checklist as ChecklistJson;
  }, [row.checklist]);

  const applyPatch = useCallback(
    async (patch: SopPatch) => {
      setUpdating(true);
      setError(null);
      try {
        await onPatch(row.sop_id, patch);
      } catch (e: unknown) {
        // Surface the real DB / network error so it's actionable, not a
        // generic "Could not save change." (#bug: numeric blank coercion).
        const parts: string[] = [];
        if (e && typeof e === 'object') {
          const err = e as Record<string, unknown>;
          if (typeof err.message === 'string') parts.push(err.message);
          if (typeof err.details === 'string') parts.push(err.details);
          if (typeof err.hint === 'string') parts.push(`Hint: ${err.hint}`);
        }
        setError(parts.length > 0 ? parts.join(' — ') : 'Could not save change.');
      } finally {
        setUpdating(false);
      }
    },
    [onPatch, row.sop_id],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end bg-tcs-scrim/60 sm:items-stretch"
      {...overlayClickClose(onClose)}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[96vh] w-full flex-col overflow-hidden rounded-t-xl bg-tcs-overlay shadow-xl sm:max-h-none sm:w-[560px] sm:rounded-none sm:rounded-l-xl"
        role="dialog"
        aria-modal="true"
      >
        <Header row={row} onClose={onClose} />

        {error && (
          <p className="mx-4 mt-2 rounded-md border border-rag-red bg-st-danger-bg px-3 py-2 text-xs text-rag-red">
            {error}
          </p>
        )}

        {cardClosed && <ClosedBanner row={row} />}

        {/* Tab bar */}
        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-tcs-border bg-tcs-bg px-3 py-2">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`shrink-0 rounded-md px-3 py-1.5 text-xs transition ${
                  active
                    ? 'bg-tcs-inverse text-tcs-inverse-ink'
                    : 'bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
                }`}
                aria-selected={active}
                role="tab"
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {tab === 'info' && (
            <InfoTab
              row={row}
              onPatch={applyPatch}
              disabled={locked}
              onGenerateSummary={() => setShowSummary(true)}
              onStartChanged={() => {
                onRefetch?.();
                onClose();
              }}
            />
          )}
          {tab === 'pricing' && (
            <PricingTab row={row} onPatch={applyPatch} disabled={locked} />
          )}
          {tab === 'contractor' && (
            <ContractorTab
              row={row}
              onPatch={applyPatch}
              onSaved={onRefetch}
              disabled={locked}
            />
          )}
          {tab === 'checklist' && (
            <ChecklistTab
              row={row}
              checklist={checklist}
              onPatch={applyPatch}
              disabled={locked}
            />
          )}
          {tab === 'attachments' && (
            <AttachmentsTab row={row} onPatch={applyPatch} disabled={locked} />
          )}
          {tab === 'activity' && <ActivityTab sopId={row.sop_id} />}
        </div>

        {/* The two sign-offs, with the button that records them. Above the
            footer and outside the tabs, because a My Work card that says
            "Executive review sign-off needed" links straight here. */}
        <SignOffStrip row={row} onRefetch={onRefetch} />

        {/* AFB-0064 (Ben, 2026-08-06): a card leaves the New Accounts board ONLY
            here. Completion and age no longer evict it, because the board is
            where Jose audits what is still missing.

            ⛔ AND THIS IS THE CLOSE (Ben, 2026-09-11). Archiving is what ends the
            card's ability to change anything — completing the checklist is the
            middle of onboarding, not the end. Finance can now do it: it was
            superuser-only, which is Ben and Bryan, so Jose — the one person who
            actually performs this step — could not. It stays a deliberate,
            named, reversible act. */}
        {canArchiveNewAccountCard(profile) && (
          <div className="flex items-center justify-between gap-3 border-t border-tcs-border px-4 py-2">
            <span className="text-[11px] text-tcs-text-secondary">
              {row.archived_at
                ? 'Archived — hidden from the board, still in the Archived lens.'
                : 'This card stays on the board until you archive it.'}
            </span>
            <button
              type="button"
              disabled={lifecycleBusy}
              onClick={async () => {
                setError(null);
                setUpdating(true);
                let rpcErr: unknown = null;
                try {
                  await archiveSopCard(row.sop_id, !row.archived_at);
                } catch (e) {
                  rpcErr = e;
                }
                setUpdating(false);
                if (rpcErr) {
                  // errorMessage(), not `err instanceof Error` — a PostgrestError
                  // is not an Error and collapses to a useless "Save failed"
                  // (the pattern corrected in PR #176).
                  setError(errorMessage(rpcErr, 'Could not change the archive state.'));
                  return;
                }
                onRefetch?.();
                onClose();
              }}
              className="min-h-[44px] shrink-0 rounded-md border border-tcs-border-strong px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:border-tcs-text-muted hover:text-tcs-text disabled:opacity-50"
            >
              {row.archived_at ? 'Restore to board' : 'Archive card'}
            </button>
          </div>
        )}

        <Footer
          row={row}
          onPatch={applyPatch}
          disabled={lifecycleBusy}
          onDelete={
            onDelete && canDelete
              ? (reason) => onDelete(row.sop_id, reason)
              : undefined
          }
        />
      </aside>

      {showSummary && (
        <SummaryModal row={row} onClose={() => setShowSummary(false)} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sign-off strip
//
// Ben, 2026-08-24: "I still cannot clear 'Executive review not recorded' … IT
// MUST BE CLEARABLE. BOTH it goes away when the information is added AND I can
// dismiss."
//
// The dismiss half is generic now (my_work_dismiss). This is the other half.
// exec_review_date and confirmation_call_date have existed since the schema was
// extended and were set on 0 of 38 rows EVER, because no screen could write
// them: v_account_open_requirements asked for a signature and the app offered
// no pen. This is the pen. It sits ABOVE the tabs — not inside one — because
// the person who arrives here came from a My Work card that named exactly this.
//
// The gate is enforced in sop_record_signoff(); what follows is the courtesy
// copy, so a viewer who cannot sign is told who can instead of being handed a
// button that fails.
// ----------------------------------------------------------------------------

const STAGE_RANK: Record<string, number> = {
  Info_and_Sub_Checklist: 1,
  Centralized_Confirmation_Call: 2,
  Executive_Review: 3,
  Final_Pre_Invoicing_Check: 4,
  Completed: 5,
};

interface SignOff {
  kind: SopSignoffKind;
  label: string;
  /** Stage this becomes due at — the same threshold the requirement view uses. */
  dueAtRank: number;
  date: string | null;
  byEid: string | null;
  mayISign: boolean;
  whoInstead: string;
}

function SignOffStrip({
  row,
  onRefetch,
}: {
  row: NewAccountSop;
  onRefetch?: () => void;
}) {
  const { employee, profile } = useAuth();
  const role = profile?.role ?? null;
  const myEid = employee?.employee_id ?? null;
  const isLeadership = hasFullAmAuthority(role);

  const [busy, setBusy] = useState<SopSignoffKind | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  // ⛔ The server accepts the confirmation call from COALESCE(facilities
  // .account_manager_eid, new_account_sop.am_eid) — the SOP row only carries
  // the second. Checking am_eid alone would hide the button from the very AM
  // the RPC would have let through, so the facility's AM is read too. A UI
  // narrower than the rule it mirrors is its own kind of dead end.
  //
  // 2026-09-10: this fetch became the shared resolver, because the contractor
  // tab needs exactly the same answer now that the card no longer stores its
  // own second copy.
  const { eid: facAm } = useFacilityAccountManager(row.facility_id);
  const isThisAccountsAm = !!myEid && (facAm === myEid || (!facAm && row.am_eid === myEid));

  const rank = STAGE_RANK[row.stage ?? ''] ?? 0;
  // Completed accounts are DONE being asked (migration
  // sign_off_requirements_stop_haunting_completed_accounts, D1=C). Showing the
  // strip there would re-open a question the business already closed.
  const isCompleted = (row.stage ?? '') === 'Completed';

  const signOffs: SignOff[] = useMemo(
    () => [
      {
        kind: 'confirmation_call',
        label: 'Confirmation call',
        dueAtRank: 2,
        date: row.confirmation_call_date,
        byEid: row.confirmation_call_by,
        mayISign: isLeadership || isThisAccountsAm,
        whoInstead: "this account's manager or leadership",
      },
      {
        kind: 'exec_review',
        label: 'Executive review',
        dueAtRank: 3,
        date: row.exec_review_date,
        byEid: row.exec_reviewer_eid,
        mayISign: isLeadership,
        whoInstead: 'a national AM, an executive, or a superuser',
      },
    ],
    [
      row.confirmation_call_date,
      row.confirmation_call_by,
      row.exec_review_date,
      row.exec_reviewer_eid,
      isLeadership,
      isThisAccountsAm,
    ],
  );

  // Only the two rows worth drawing: due at this stage, or already recorded.
  const shown = signOffs.filter((s) => !isCompleted && (rank >= s.dueAtRank || s.date));

  const eidsToName = shown.map((s) => s.byEid).filter((e): e is string => !!e);
  const wantNames = eidsToName.join(',');
  useEffect(() => {
    if (!wantNames) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('v_am_name')
        .select('employee_id, display_name')
        .in('employee_id', wantNames.split(','));
      if (cancelled || !data) return;
      setNames(
        Object.fromEntries(
          (data as { employee_id: string; display_name: string | null }[]).map((r) => [
            r.employee_id,
            r.display_name ?? r.employee_id,
          ]),
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [wantNames]);

  if (shown.length === 0) return null;

  async function act(kind: SopSignoffKind, clear: boolean) {
    setBusy(kind);
    setErr(null);
    try {
      await recordSopSignoff(row.sop_id, kind, { clear });
      onRefetch?.();
    } catch (e) {
      setErr(errorMessage(e, 'That sign-off did not save.', 'new-accounts/sign-off'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="shrink-0 border-t border-tcs-border bg-tcs-bg px-4 py-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
        Sign-offs
      </h4>
      {err && (
        <p className="mt-1 rounded-md border border-rag-red bg-st-danger-bg px-2 py-1 text-[11px] text-rag-red">
          {err}
        </p>
      )}
      <ul className="mt-1 grid gap-1">
        {shown.map((s) => (
          <li key={s.kind} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-tcs-text-secondary">
              <span className="font-medium text-tcs-text">{s.label}</span>
              {s.date ? (
                <>
                  {' · recorded '}
                  {fmtBareDate(s.date)}
                  {s.byEid ? ` by ${names[s.byEid] ?? s.byEid}` : ''}
                </>
              ) : s.mayISign ? (
                ' · not recorded'
              ) : (
                ` · not recorded — recorded by ${s.whoInstead}`
              )}
            </span>
            {s.mayISign && (
              <button
                type="button"
                disabled={busy === s.kind}
                onClick={() => void act(s.kind, !!s.date)}
                className="min-h-[32px] shrink-0 rounded-md border border-tcs-border-strong px-2.5 text-xs font-medium text-tcs-text-secondary hover:border-tcs-text-muted hover:text-tcs-text disabled:opacity-50"
              >
                {s.date ? 'Undo' : `Record ${s.label.toLowerCase()}`}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Header
// ----------------------------------------------------------------------------

function Header({ row, onClose }: { row: NewAccountSop; onClose: () => void }) {
  const stageLabel =
    (NEW_ACCOUNT_SOP_STAGE_LABELS as Record<string, string>)[row.stage ?? ''] ??
    row.stage ??
    '—';
  // Reads the market's own row rather than the card's stored copy of it.
  const { markets } = useMarkets();
  const marketColor = marketColorPair(markets, row.market_region).base;

  return (
    <header className="flex items-start justify-between gap-3 border-b border-tcs-border bg-tcs-bg p-4">
      <div className="min-w-0">
        <h2 className="truncate text-lg font-semibold text-tcs-text">
          {resolveCardName(row).primary}
        </h2>
        <p className="mt-0.5 text-xs text-tcs-text-muted">
          {row.sop_id}
          {` · ${resolveCardName(row).customer ?? SINGLE_SITE_CUSTOMER_LABEL}`}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded bg-tcs-surface px-1.5 py-0.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: marketColor }}
            />
            {row.market_region || '—'}
          </span>
          {row.facility_type && (
            <span className="rounded bg-tcs-surface px-1.5 py-0.5">
              {(NAS_FACILITY_TYPE_LABELS as Record<string, string>)[row.facility_type] ?? row.facility_type}
            </span>
          )}
          <span className="rounded bg-tcs-inverse px-1.5 py-0.5 text-tcs-inverse-ink">
            {stageLabel}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-md border border-tcs-border bg-tcs-raised p-1.5 text-tcs-text-secondary hover:bg-tcs-bg-muted"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </header>
  );
}

// ----------------------------------------------------------------------------
// Shared data hooks for the editable tabs (eligibility-filtered pickers #5,
// active-sub picker #7).
// ----------------------------------------------------------------------------

interface EmpOpt {
  employee_id: string;
  name: string;
}

// ⛔ 2026-09-03. This hook used to destructure only the DATA half of each
// response and never look at `error`, so `data ?? []` turned a REFUSED read
// into an empty list. The AM dropdown then rendered as though the company
// employs no account managers, with no banner and no console line, and the
// reporter of the New Account card ticket correctly said "I can't assign an
// AM" — there was nobody in the list to assign.
//
// The refusal is real and is not a bug in this file: the view behind the AM
// half gates on the CALLER's role, and a `sales` user is not in that list, so
// it returns zero rows for them. Whether that gate is right is a permissions
// question and not this run's. What IS this file's job is telling the truth
// about it: an empty list and a denied read are different states and the
// person must be able to tell them apart. Never swallow an error to NULL (§8.8).
function useEligibleEmployees() {
  const [bdd, setBdd] = useState<EmpOpt[]>([]);
  const [am, setAm] = useState<EmpOpt[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = (rows: { employee_id: string; employee_name_first: string | null; employee_name_last: string | null }[]) =>
        rows.map((r) => ({
          employee_id: r.employee_id,
          name: [r.employee_name_first, r.employee_name_last].filter(Boolean).join(' ').trim() || r.employee_id,
        }));
      const [{ data: b, error: be }, { data: a, error: ae }] = await Promise.all([
        // BDD half: v_employee_picker (TCSD-0152) — Active AND
        // pay_type='Salaried' AND app_role IS NOT NULL. PICKERS EXCLUDE,
        // RESOLVERS RESOLVE (TCSD-0129). ⛔ Do not widen this back to
        // v_employees_am, which is the FULL roster. employment_status is gone:
        // the view has no such column (naming one fails at RUNTIME, not at
        // build) and every row in it is Active by definition. The activity-log
        // fetch further down this file is the RESOLVER half and stays put.
        supabase.from('v_employee_picker').select('employee_id, employee_name_first, employee_name_last').eq('is_bdd_eligible', true).order('employee_name_first'),
        // AM half: v_am_assignable — one shared definition of "can be an AM",
        // same as the create modal and the facility-detail picker. CW-L PR 3.
        supabase.from('v_am_assignable').select('employee_id, employee_name_first, employee_name_last').order('employee_name_first'),
      ]);
      if (cancelled) return;
      setBdd(map(b ?? []));
      setAm(map(a ?? []));
      // An empty list is a fact about the company. A refused read is a fact
      // about you. Say which one this is, and name the half that failed so
      // the sentence matches the dropdown the person is staring at.
      const refused: string[] = [];
      if (be) refused.push('BDD');
      if (ae) refused.push('AM');
      if (refused.length > 0) {
        console.error('[new-account-sop] owner picker read refused', { bdd: be, am: ae });
        setOptionsError(
          `The ${refused.join(' and ')} ${refused.length > 1 ? 'lists' : 'list'} could not be ` +
            `loaded, so ${refused.length > 1 ? 'they are' : 'it is'} showing as empty. This is a ` +
            `permission on your account, not a problem with this card — ask for the ` +
            `${refused.join('/')} picker to be opened up for your role.`,
        );
      } else {
        setOptionsError(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { bdd, am, optionsError };
}

/**
 * The account manager ON THE BUILDING — the one answer, once a building exists.
 *
 * ⛔ The card's own column is the intake buffer and stops being the answer the
 * moment the card materializes. Two surfaces need this: the sign-off strip
 * (whose gate the server evaluates as
 * COALESCE(facilities.account_manager_eid, new_account_sop.am_eid)) and the
 * contractor tab, which now reads through instead of offering a second editor.
 * One fetch, one resolver — the name comes from v_am_name because
 * `employees` RLS refuses the roster to AM and sales roles.
 */
function useFacilityAccountManager(facilityId: string | null | undefined): {
  eid: string | null;
  name: string | null;
  loading: boolean;
} {
  const [eid, setEid] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(facilityId));

  useEffect(() => {
    if (!facilityId) {
      setEid(null);
      setName(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('facilities')
        .select('account_manager_eid')
        .eq('facility_id', facilityId)
        .maybeSingle();
      if (cancelled) return;
      const found =
        (data as { account_manager_eid: string | null } | null)
          ?.account_manager_eid ?? null;
      setEid(found);
      if (found) {
        const map = await fetchAmNameMap([found]);
        if (!cancelled) setName(map.get(found) ?? found);
      } else {
        setName(null);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  return { eid, name, loading };
}

function EmployeeSelect({
  label,
  value,
  options,
  onSave,
  disabled,
}: {
  label: string;
  value: string;
  options: EmpOpt[];
  onSave: (v: string | null) => Promise<void> | void;
  disabled?: boolean;
}) {
  // ⛔ An empty option list must not look like a working dropdown. The AM view
  // returns ZERO ROWS — with no error — for a caller whose role is not on its
  // list, so the refused-read banner above cannot catch this case: there is
  // nothing to catch. The only honest signal left is the dropdown itself. It
  // says nobody is available and why that is not the same as "unassigned",
  // which is the difference between "I can't assign an AM" and "the app won't
  // save" — and the reporter had no way to tell us which one he meant.
  const empty = options.length === 0;
  return (
    <FieldShell label={label}>
      <select
        value={value}
        onChange={(e) => onSave(e.target.value || null)}
        disabled={disabled || empty}
        className={FIELD_INPUT_CLS}
        aria-describedby={empty ? `${label}-empty` : undefined}
      >
        <option value="">{empty ? 'Nobody available to choose' : 'Unassigned'}</option>
        {options.map((o) => (
          <option key={o.employee_id} value={o.employee_id}>
            {o.name}
          </option>
        ))}
      </select>
      {empty && (
        <p id={`${label}-empty`} className="mt-1 text-xs text-tcs-text-muted">
          This list is empty for your account, so there is nobody to assign. It is not that the save
          failed — ask for this picker to be opened up for your role.
        </p>
      )}
    </FieldShell>
  );
}

// ----------------------------------------------------------------------------
// Tab 1: Info — editable headline fields. Each field saves on blur.
// ----------------------------------------------------------------------------

function InfoTab({
  row,
  onPatch,
  disabled,
  onGenerateSummary,
  onStartChanged,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
  onGenerateSummary: () => void;
  onStartChanged: () => void;
}) {
  const { bdd, optionsError } = useEligibleEmployees();
  const { employee } = useAuth();
  const { markets, error: marketsError } = useMarkets();
  // ⛔ Say which answer the reader is looking at. These fields live on the
  // building now; where the building has none, the intake's value is showing
  // through and it is the ONLY answer anybody has. Naming that is the whole
  // reason it is a column on the row and not a COALESCE nobody can see.
  const intakeOnly = row.intake_only_fields ?? [];
  return (
    <div className="grid gap-3">
      {intakeOnly.length > 0 && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-muted">
          Still answered by this form alone, because{' '}
          {row.facility_name ?? 'the building'} has no value of its own yet:{' '}
          <span className="font-medium text-tcs-text">
            {intakeOnly.map((f) => f.replace(/_/g, ' ')).join(', ')}
          </span>
          . Saving here puts the answer on the building.
        </p>
      )}
      {optionsError && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-muted">
          {optionsError}
        </p>
      )}
      {marketsError && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-muted">
          The market list could not be loaded, so the market picker is empty.
          Reload before changing it — saving an empty market would clear the one
          this card already has.
        </p>
      )}
      {/* Account start — confirm go-live + editable actual start date (PR-4a). */}
      <AccountStartPanel
        facilityId={row.facility_id}
        plannedStartDate={row.start_date ?? null}
        actualStartDate={row.billing_active_date ?? null}
        isStarted={!!row.started_confirmed_at}
        onDone={onStartChanged}
      />
      {/* Go-live (AM operational) checklist — PR-6. Auto-archives 7d after start. */}
      <StartupChecklist
        value={row.startup_checklist ?? null}
        onSave={(next) => onPatch({ startup_checklist: next })}
        currentEid={employee?.employee_id}
        archived={isStartupArchived(row.started_confirmed_at)}
        disabled={disabled}
      />
      {/* 2026-08-26, TCSD-0118 / TCSD-0121. This was an editable "Account name"
          text box, and it was a lie: typing in it wrote new_account_sop.account_name
          and did NOT change the account the card points at. It looked like renaming
          the customer and never was. The customer is CHOSEN — the intake's picker
          sets account_id — so this now shows who that is and says where it comes
          from. ⚠️ There is no picker on this drawer yet, so a card linked to the
          wrong customer still cannot be repointed from here; that is a real gap and
          it is on Ben's list, not something a text box was ever fixing. */}
      <div data-field="account_name">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
          Customer
        </div>
        <div className="mt-1 text-sm text-tcs-text">
          {row.account_name?.trim() || '—'}
        </div>
        <p className="mt-0.5 text-[11px] text-tcs-text-muted">
          {row.account_id
            ? `Linked account ${row.account_id}. Renaming the customer is done on the account, not here.`
            : 'Typed at intake — this card is not linked to an account yet.'}
        </p>
      </div>
      <SelectField
        label="Facility type"
        value={row.facility_type ?? ''}
        options={[
          { value: '', label: '—' },
          ...NAS_FACILITY_TYPE_OPTIONS.map((t) => ({
            value: t,
            label: NAS_FACILITY_TYPE_LABELS[t as NewAccountSopFacilityType],
          })),
        ]}
        onSave={(v) => onPatch({ facility_type: v || null })}
        disabled={disabled}
      />
      {/* AFB-0032 (Jack) — OR/PR room counts are critical surgery-center info.
          Captured here so they ride along with the new account and surface on
          the facility Overview once live. Shown for surgical facility types. */}
      {(row.facility_type === 'Surgery_Center' ||
        row.facility_type === 'Hospital' ||
        row.or_count != null ||
        row.pr_count != null) && (
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label="OR count"
            value={row.or_count ?? null}
            onSave={(v) => onPatch({ or_count: v })}
            disabled={disabled}
          />
          <NumberField
            label="PR count"
            value={row.pr_count ?? null}
            onSave={(v) => onPatch({ pr_count: v })}
            disabled={disabled}
          />
        </div>
      )}
      {/* Options come from public.markets, so a market added by a row appears
          here with no deploy. ⛔ market_color is NOT sent: the colour has one
          home on the market's row and the DB trigger keeps the card's copy in
          step. The app writing it is how one card came to store Houston's dot
          on an Austin market. */}
      <SelectField
        label="Market region"
        value={row.market_region ?? ''}
        options={[
          { value: '', label: '—' },
          ...marketOptions(markets).map((m) => ({ value: m, label: m })),
        ]}
        onSave={(v) => onPatch({ market_region: v || null })}
        disabled={disabled}
      />
      <DebouncedTextField
        label="Start date"
        type="date"
        value={row.start_date ?? ''}
        onSave={(v) => onPatch({ start_date: v || null })}
        disabled={disabled}
      />
      <EmployeeSelect
        label="BDD (Client & Billing owner)"
        value={row.bdd_eid ?? ''}
        options={bdd}
        onSave={(v) => onPatch({ bdd_eid: v })}
        disabled={disabled}
      />
      <DebouncedTextArea
        label="Notes"
        value={row.notes ?? ''}
        onSave={(v) => onPatch({ notes: v || null })}
        disabled={disabled}
      />

      {/* #5 — compile the card into a copyable Antonela→Amy billing-setup
          email. Reads from the row, so it works regardless of which tab the
          fields were filled on. */}
      <button
        type="button"
        onClick={onGenerateSummary}
        className="mt-1 inline-flex items-center justify-center gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted"
      >
        <Copy className="h-4 w-4" /> Generate summary
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab 2: Pricing & Billing
// ----------------------------------------------------------------------------

// AFB-0055 — read-only pricing economics. Client price, sub cost, profit and
// margin, computed client-side. Margin is tinted against the 26.5% target used
// elsewhere in the app (green ≥ target, amber between 0 and target, red ≤ 0).
// Degrades gracefully when sub payment is null/0 — shows "—" + a hint and never
// renders a bogus 100% margin.
function PricingEconomics({
  clientPrice,
  subPayment,
  hourly = null,
}: {
  clientPrice: number | null | undefined;
  subPayment: number | null | undefined;
  /** Items 831/758: the card's lines are billed by the hour (hourlyCardRates). */
  hourly?: { client: number | null; sub: number | null } | null;
}) {
  const price = typeof clientPrice === 'number' && clientPrice > 0 ? clientPrice : null;
  const sub = typeof subPayment === 'number' && subPayment > 0 ? subPayment : null;
  const hasBoth = price != null && sub != null;
  const profit = hasBoth ? price - sub : null;
  const marginPct = hasBoth ? ((price - sub) / price) * 100 : null;

  function marginClass(m: number | null): string {
    if (m == null) return 'text-tcs-text-muted';
    if (m <= 0) return 'text-rag-red';
    if (m >= 26.5) return 'text-rag-green';
    return 'text-st-warn-fg';
  }

  // ⛔ An hourly card says its money per hour, from the line's own rates. No
  // "/ mo" anywhere in the box: there is no monthly figure (Ben 2026-10-12).
  if (hourly) {
    const e = hourlyEconomics(hourly.client, hourly.sub);
    // Cents kept: a per-hour figure rounded to the dollar is a different rate.
    const cell = (n: number | null) =>
      n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
    return (
      <div className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2" data-field="economics_hourly">
        <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
          <span>
            Economics <span className="font-normal normal-case text-tcs-text-muted">· read-only</span>
          </span>
          <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-tcs-text">
            {rateBasisChip('hourly')}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-[11px] text-tcs-text-muted">Client / hour</dt>
            <dd className="font-medium text-tcs-text">{cell(e.clientPerHour)}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-tcs-text-muted">Sub / hour</dt>
            <dd className="font-medium text-tcs-text">{cell(e.subPerHour)}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-tcs-text-muted">Profit / hour</dt>
            <dd className="font-medium text-tcs-text">{cell(e.profitPerHour)}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-tcs-text-muted">Margin</dt>
            <dd className={`font-semibold ${marginClass(e.marginPct)}`}>
              {e.marginPct != null ? `${e.marginPct.toFixed(1)}%` : '—'}
            </dd>
          </div>
        </dl>
        <p className="mt-1 text-[11px] text-tcs-text-muted">{HOURLY_ECONOMICS_CAPTION}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
        Economics <span className="font-normal normal-case text-tcs-text-muted">· read-only</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-[11px] text-tcs-text-muted">Client / mo</dt>
          <dd className="font-medium text-tcs-text">{fmtSummaryMoney(price)}</dd>
        </div>
        <div>
          <dt className="text-[11px] text-tcs-text-muted">Sub $ / mo</dt>
          <dd className="font-medium text-tcs-text">
            {sub != null ? (
              fmtSummaryMoney(sub)
            ) : (
              <span
                className="text-tcs-text-muted"
                title="Set sub payment on the Contractor tab"
              >
                — set sub payment
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] text-tcs-text-muted">Profit / mo</dt>
          <dd className="font-medium text-tcs-text">
            {profit != null ? fmtSummaryMoney(profit) : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] text-tcs-text-muted">Margin</dt>
          <dd className={`font-semibold ${marginClass(marginPct)}`}>
            {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PricingTab({
  row,
  onPatch,
  disabled,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
}) {
  // ⛔ A PRICE BOX MUST NOT EXIST ALONGSIDE THE LINES THAT DECIDE THE PRICE.
  //
  // Ben, 2026-09-11, on Houston Physicians: "Why is there now only one 'client
  // rate', which should not even exist without service lines?" He is right. The
  // client price is the SUM OF THE SERVICE LINES — the database derives the
  // building's rate from them on every line change — so a second box offering
  // to set it by hand is a second answer to a settled question. Whoever types
  // in it is either overwritten by the next line edit or silently disagreeing
  // with the lines.
  //
  // So: with lines, the total is SHOWN and the lines are where you change it.
  // Without lines (27 of 42 live cards today, all legacy), the box remains the
  // only way to record a price and stays editable — removing it there would
  // take away the only pen.
  const lineCount = Array.isArray(row.service_lines) ? row.service_lines.length : 0;
  const pricedByLines = lineCount > 0;
  // Items 831/758: a card whose lines are billed by the hour has no monthly price.
  const hourly = hourlyCardRates(row.service_lines);
  return (
    <div className="grid gap-3">
      {hourly ? (
        <FieldShell label="Client rate" hint={HOURLY_CARD_PRICE_HINT}>
          <output className="block rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-sm tabular-nums text-tcs-text">
            {hourlyBilledText(hourly.client, { cents: true })}
          </output>
        </FieldShell>
      ) : pricedByLines ? (
        <FieldShell
          label="Client Monthly Price ($)"
          hint={`The sum of this account's ${lineCount} service line${
            lineCount === 1 ? '' : 's'
          }. Change a line's rate on the Contractor tab — it is not typed here.`}
        >
          <output className="block rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-sm tabular-nums text-tcs-text">
            {row.final_pricing == null
              ? '—'
              : `$${Number(row.final_pricing).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
          </output>
        </FieldShell>
      ) : (
        <NumberField
          label="Client Monthly Price ($)"
          hint="Agreed pre-tax rate the facility pays us."
          value={row.final_pricing}
          onSave={(v) => onPatch({ final_pricing: v })}
          disabled={disabled}
        />
      )}
      {/* AFB-0055 — read-only economics next to Client price so Finance can see
          sub cost + margin on the Pricing tab (the estimator lives only on the
          Contractor tab). Mirrors the project-work margin display (AFB-0007):
          profit plain, margin tinted, "—" when sub payment is unset. */}
      <PricingEconomics
        clientPrice={row.final_pricing}
        subPayment={row.sub_payment}
        hourly={hourly}
      />

      {/* AFB-0081 (Jose Azocar, 2026-08-17): "the card when created does ask
          those questions but the summary is not showing that". Both answers were
          captured at intake and then shown back on no screen at all —
          management_fee_pct appeared nowhere in this file, and
          tcs_provides_usables was not even in SopEditableField. Editable here,
          not just visible: the Phase 6b gate asks for these two answers, and a
          gate on a field nobody can reach is a dead end. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <FeePercentField
          value={row.management_fee_pct}
          onSave={(v) => onPatch({ management_fee_pct: v })}
          disabled={disabled}
        />
        <SelectField
          label="Usables account?"
          value={toUsablesChoice(row.tcs_provides_usables)}
          options={USABLES_OPTIONS}
          onSave={(v) => onPatch({ tcs_provides_usables: fromUsablesChoice(v) })}
          disabled={disabled}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <DebouncedTextField
          label="Payment terms"
          value={row.payment_terms ?? ''}
          onSave={(v) => onPatch({ payment_terms: v || null })}
          disabled={disabled}
        />
        <DebouncedTextField
          label="Payment frequency"
          value={row.payment_frequency ?? ''}
          onSave={(v) => onPatch({ payment_frequency: v || null })}
          disabled={disabled}
        />
        {/* #4 — Service days/wk is a client billing/service term; moved here
            from the Contractor tab. */}
        <NumberField
          label="Service days / wk"
          hint="Cleaning cadence the client is billed for."
          value={row.service_days_per_week}
          onSave={(v) => onPatch({ service_days_per_week: v })}
          disabled={disabled}
        />
      </div>

      <h3 className="mt-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        Square footage <span className="font-normal normal-case text-tcs-text-muted">· BDD + AM</span>
      </h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <NumberField label="Total sq ft" value={row.total_sqft} onSave={(v) => onPatch({ total_sqft: v })} disabled={disabled} />
        <NumberField label="Sterile sq ft" value={row.sterile_sqft} onSave={(v) => onPatch({ sterile_sqft: v })} disabled={disabled} />
        <NumberField label="Non-sterile sq ft" value={row.non_sterile_sqft} onSave={(v) => onPatch({ non_sterile_sqft: v })} disabled={disabled} />
      </div>

      <h3 className="mt-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        Billing contact
      </h3>
      <DebouncedTextField
        label="Name"
        value={row.billing_contact_name ?? ''}
        onSave={(v) => onPatch({ billing_contact_name: v || null })}
        disabled={disabled}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <DebouncedTextField
          label="Email"
          type="email"
          value={row.billing_contact_email ?? ''}
          onSave={(v) => onPatch({ billing_contact_email: v || null })}
          disabled={disabled}
        />
        <DebouncedTextField
          label="Phone"
          type="tel"
          value={row.billing_contact_phone ?? ''}
          onSave={(v) => onPatch({ billing_contact_phone: v || null })}
          disabled={disabled}
        />
      </div>

      <h3 className="mt-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        Client address
      </h3>
      {/* ⛔ The four boxes are the INTAKE, and only the intake. The building
          holds one address line, so four boxes cannot round-trip it: whatever
          was typed here after the building existed became a second answer that
          had to be reconciled. Once the building exists this is one field,
          writing the building, and there is nothing to reconcile. The state box
          also picks the market, which is why the four survive for a draft. */}
      {row.facility_id ? (
        <DebouncedTextField
          label="Address"
          value={row.facility_address ?? ''}
          onSave={(v) => onPatch({ facility_address: v || null })}
          disabled={disabled}
          hint="Saved on the building. The building's own card shows the same line."
        />
      ) : (
        <>
          <DebouncedTextField
            label="Street"
            value={row.client_address_street ?? ''}
            onSave={(v) => onPatch({ client_address_street: v || null })}
            disabled={disabled}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <DebouncedTextField
              label="City"
              value={row.client_address_city ?? ''}
              onSave={(v) => onPatch({ client_address_city: v || null })}
              disabled={disabled}
            />
            <DebouncedTextField
              label="State"
              value={row.client_address_state ?? ''}
              onSave={(v) => onPatch({ client_address_state: v || null })}
              disabled={disabled}
            />
            <DebouncedTextField
              label="ZIP"
              value={row.client_address_zip ?? ''}
              onSave={(v) => onPatch({ client_address_zip: v || null })}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab 3: Contractor
// ----------------------------------------------------------------------------

function ContractorTab({
  row,
  onPatch,
  onSaved,
  disabled,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  // The service lines are written on the BUILDING now, not on the card, so a
  // line save does not go through onPatch and nothing it touches would refetch
  // on its own. This is that refetch.
  onSaved?: () => void;
  disabled: boolean;
}) {
  const { am, optionsError: amOptionsError } = useEligibleEmployees();
  const { name: facAmName, loading: facAmLoading } = useFacilityAccountManager(
    row.facility_id,
  );
  // ⛔ NO SUB MODE, NO MARGIN SLIDER, NO CREATE-A-SUB PANEL HERE. All three
  // belonged to the editor removed below, and each one is answered PER LINE by
  // the lines editor: a building can have a nightly crew and a day porter on
  // different subs at different rates, so "the sub" and "the sub payment" were
  // never single-valued questions this tab could ask.

  // Service lines — EDITABLE here since 2026-09-08.
  //
  // This block used to render the lines read-only and print a sentence telling
  // the reader to "edit the full set on the account card once the facility is
  // live." Nothing anywhere kept that promise: a card created with one line
  // could never gain a second and a card created with the wrong line could
  // never lose it, so an AM who needed a day porter alongside a nightly crew
  // opened a SECOND onboarding card for the same Houston hospital. The editor
  // the sentence pointed at is now this one.
  const serviceLines = useMemo(
    () =>
      Array.isArray(row.service_lines) && row.service_lines.length > 0
        ? serviceLinesFromRow(row)
        : [],
    [row],
  );

  return (
    <div className="grid gap-3">
      {amOptionsError && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-muted">
          {amOptionsError}
        </p>
      )}
      <ServiceLinesEditor
        row={row}
        initialLines={serviceLines}
        onSaved={onSaved}
        disabled={disabled}
      />
      {/* ⛔ ONE SOURCE OF TRUTH FOR THE ACCOUNT MANAGER — Ben, 2026-09-09.
          Before the card materializes there is no building, so the card IS the
          record and this is the editor. The moment a building exists, the
          BUILDING owns the manager: it is what the app routes on, what the
          protected-columns guard defends, and what every downstream surface
          reads. Leaving an editor here made a second stored answer that nothing
          reconciled — twelve live buildings had a card naming a different
          manager, every one of them a territory handover the card never heard
          about. So after materialization this reads THROUGH to the building and
          the edit happens there. Same shape as the customer name, which
          resolves through the account rather than trusting the card's copy. */}
      {row.facility_id ? (
        <FieldShell label="AM (Contractor owner)">
          <div className="text-sm">
            {facAmLoading ? (
              <span className="text-tcs-text-muted">Reading the building…</span>
            ) : (
              <>
                <span>{facAmName ?? 'Nobody recorded yet'}</span>
                <span className="ml-2 text-xs text-tcs-text-muted">
                  on the building — change it there
                </span>
              </>
            )}
          </div>
        </FieldShell>
      ) : (
        <EmployeeSelect
          label="AM (Contractor owner)"
          value={row.am_eid ?? ''}
          options={am}
          onSave={(v) => onPatch({ am_eid: v })}
          disabled={disabled}
        />
      )}

      {/* ⛔ THE SECOND SUBCONTRACTOR EDITOR IS GONE — Ben, 2026-09-14.
          Jack: "there is still 2 places to put subs on the app under new
          accounts. The top item is a new one and you can't assign a price or
          anything. The bottom one is the old one and functions as needed."

          The bottom one did NOT function. It wrote new_account_sop's
          service_provider_id / sub_payment / no_miss_incentive — which stopped
          being answers on 2026-09-08 and became a MIRROR of the service lines
          — and nothing downstream has read them since. Payroll reads the
          building's line. The sub's own list of buildings reads the building's
          line. The onboarding colour asks the building's line. So a value typed
          here stuck to the card, looked saved, and paid nobody.

          NS Surgical Center (Amy Peralta / Vimex) is what that costs: the card
          held P-0117 at $1,035 while ASN-00268 carried no sub and no rate from
          1 Sep. Worse, the card's copy SILENCED the two open-requirement chips
          that exist to catch exactly this — they only raise when the card is
          blank too — so nothing warned anybody for six weeks.

          One home. The lines editor above is it: it writes through
          save_service_line(), which is the one door, decides for itself whether
          a money field is a setup or a rate change, and refuses per field in
          the database's own words. */}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab 4: Checklist — 7 stages, JSONB-backed, with auto-promote prompt.
// ----------------------------------------------------------------------------

function ChecklistTab({
  row,
  checklist,
  onPatch,
  disabled,
}: {
  row: NewAccountSop;
  checklist: ChecklistJson;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
}) {
  const [promptNextStage, setPromptNextStage] =
    useState<NewAccountSopActiveStage | null>(null);

  // AFB-0081 — non-null when the stage this prompt is offering is one the
  // intake-answers gate would refuse. Derived, not stored: the answer can change
  // under this component while the prompt is open (the Pricing tab is one click
  // away), and a stale copy would either hide a live path or offer a dead one.
  const promoteBlockedReason = promptNextStage
    ? blockedStageMoveReason(row, promptNextStage)
    : null;

  const currentDbStage = row.stage as NewAccountSopActiveStage;
  const stageKeys = Object.keys(checklist);

  // Who ticked each box. ⛔ Pickers exclude, RESOLVERS resolve — this is the
  // built resolver (v_am_name), so a former employee's approval still shows
  // their name rather than a bare id. A box ticked before the audit trail could
  // attribute it shows the date alone; it is never given a made-up approver.
  const actorNames = useTaskActorNames(
    stageKeys.flatMap((sk) => (checklist[sk] ?? []).map((i) => i.checked_by)),
  );

  // For a checklist toggle, write the entire JSONB back. Cheap (one row, ~14
  // items total). Triggers an auto-promote prompt when the toggle completes
  // the last unchecked item in the current DB stage's cluster.
  async function toggle(stageKey: string, itemKey: string) {
    const next = { ...checklist };
    const items = (next[stageKey] ?? []).map((item) => ({ ...item }));
    const idx = items.findIndex((i) => i.key === itemKey);
    if (idx < 0) return;
    const wasChecked = items[idx].checked;
    items[idx] = {
      ...items[idx],
      checked: !wasChecked,
      checked_at: !wasChecked ? new Date().toISOString() : null,
      // ⛔ checked_by is stamped by the DATABASE, from the session, on the way in
      // (trg_nas_stamp_checklist_actor). It is not sent from here and anything
      // sent is overwritten — the browser does not get to decide who approved
      // something (AFB-0078). Untick clears it, because a withdrawn approval has
      // no approver.
      checked_by: null,
    };
    next[stageKey] = items;

    await onPatch({ checklist: next });

    // If the toggle is in the current DB-stage cluster and just checked the
    // last item, prompt to advance.
    if (
      !wasChecked &&
      CHECKLIST_STAGE_TO_DB_STAGE[stageKey] === currentDbStage
    ) {
      const stageDone = stagesForDbStage(currentDbStage).every((sk) =>
        (next[sk] ?? []).every((i) => i.checked),
      );
      if (stageDone && STAGE_PROGRESSION[currentDbStage]) {
        setPromptNextStage(STAGE_PROGRESSION[currentDbStage]);
      }
    }
  }

  return (
    <div className="space-y-3">
      {stageKeys.length === 0 && (
        <p className="rounded-md border border-dashed border-tcs-border-strong p-4 text-xs text-tcs-text-muted">
          No checklist yet. New SOPs seeded after 2026-05-28 have one
          auto-populated by <span className="font-mono">nas_derive_checklist</span>.
        </p>
      )}
      {stageKeys.map((sk) => {
        const items = checklist[sk] ?? [];
        const allDone = items.length > 0 && items.every((i) => i.checked);
        const dbStage = CHECKLIST_STAGE_TO_DB_STAGE[sk];
        const isCurrent = dbStage === currentDbStage;
        return (
          <details
            key={sk}
            open={isCurrent}
            className={`rounded-md border ${
              isCurrent ? 'border-tcs-text' : 'border-tcs-border'
            } bg-tcs-raised`}
          >
            <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm font-medium">
              <span className="flex items-center gap-2">
                {CHECKLIST_STAGE_LABEL[sk] ?? sk}
                {isCurrent && (
                  <span className="rounded bg-tcs-inverse px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-inverse-ink">
                    Current
                  </span>
                )}
              </span>
              <span className="text-xs text-tcs-text-muted">
                {items.filter((i) => i.checked).length}/{items.length}
                {allDone && <Check className="ml-1 inline h-3 w-3 text-rag-green" />}
              </span>
            </summary>
            <ul className="divide-y divide-tcs-border border-t border-tcs-border">
              {items.map((item) => (
                <li key={item.key} className="flex items-start gap-2 px-3 py-2 text-sm">
                  <input
                    id={`chk-${row.sop_id}-${sk}-${item.key}`}
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggle(sk, item.key)}
                    disabled={disabled}
                    className="mt-0.5 h-4 w-4 rounded border-tcs-border-strong text-tcs-text"
                  />
                  <label
                    htmlFor={`chk-${row.sop_id}-${sk}-${item.key}`}
                    className="flex-1 select-none"
                  >
                    <span className={item.checked ? 'text-tcs-text-muted line-through' : ''}>
                      {item.label}
                    </span>
                    {item.checked && (item.checked_at || item.checked_by) && (
                      <span className="ml-2 text-[10px] text-tcs-text-muted">
                        {item.checked_at && new Date(item.checked_at).toLocaleDateString()}
                        {item.checked_by && (
                          <>
                            {item.checked_at && ' · '}
                            {actorNames[item.checked_by] ?? item.checked_by}
                          </>
                        )}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </details>
        );
      })}

      {/* Auto-promote prompt.
          AFB-0081: this is the THIRD stage write path (after the footer select
          and the Kanban drag), and it called onPatch directly. It now asks the
          same question the footer and the trigger ask — and when the answer is
          no, it says what is missing INSTEAD of offering a button that the
          database would reject. An affordance that cannot succeed is worse than
          no affordance. */}
      {promptNextStage && promoteBlockedReason && (
        <div className="rounded-md border border-rag-amber bg-st-warn-bg p-3">
          <p className="text-sm text-st-warn-fg-strong">
            The {(NEW_ACCOUNT_SOP_STAGE_LABELS as Record<string, string>)[row.stage ?? '']}{' '}
            checklist is done, but this card is not ready to move on.
          </p>
          <p className="mt-1 text-xs text-st-warn-fg-strong">{promoteBlockedReason}</p>
        </div>
      )}
      {promptNextStage && !promoteBlockedReason && (
        <div className="rounded-md border border-tcs-text bg-tcs-bg-muted p-3">
          <p className="text-sm text-tcs-text">
            All checklist items for the current stage are done. Move to{' '}
            <strong>
              {(NEW_ACCOUNT_SOP_STAGE_LABELS as Record<string, string>)[promptNextStage]}
            </strong>
            ?
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPromptNextStage(null)}
              className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs"
            >
              Not yet
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={async () => {
                const next = promptNextStage;
                setPromptNextStage(null);
                await onPatch({ stage: next });
              }}
              className="rounded-md bg-tcs-inverse px-3 py-1.5 text-xs font-medium text-tcs-inverse-ink disabled:opacity-50"
            >
              Move to next stage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------------------------------------------------------------
// Other attachments — formalized `attachments` jsonb editor (AFB-0011). Item
// shape: { type, label, drive_url, uploaded_by, uploaded_at }. URLs only.
// ----------------------------------------------------------------------------

interface SopAttachment {
  type?: string;
  label?: string;
  drive_url?: string;
  uploaded_by?: string | null;
  uploaded_at?: string;
}

function OtherAttachments({
  row,
  onPatch,
  disabled,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
}) {
  const items = useMemo<SopAttachment[]>(
    () => (Array.isArray(row.attachments) ? (row.attachments as SopAttachment[]) : []),
    [row.attachments],
  );
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  async function add() {
    if (!url.trim()) return;
    const next: SopAttachment[] = [
      ...items,
      {
        type: 'other',
        label: label.trim() || 'Attachment',
        drive_url: url.trim(),
        uploaded_by: null,
        uploaded_at: new Date().toISOString(),
      },
    ];
    await onPatch({ attachments: next });
    setLabel('');
    setUrl('');
  }

  async function remove(idx: number) {
    const next = items.filter((_, i) => i !== idx);
    await onPatch({ attachments: next });
  }

  return (
    <section className="grid gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        Other attachments
      </h3>
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((a, idx) => (
            <li
              key={`${a.drive_url}-${idx}`}
              className="flex items-center justify-between gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-xs"
            >
              <a
                href={a.drive_url}
                target="_blank"
                rel="noreferrer noopener"
                className="truncate text-tcs-accent-ink hover:underline"
              >
                {a.label || a.drive_url}
              </a>
              <button
                type="button"
                disabled={disabled}
                onClick={() => void remove(idx)}
                className="shrink-0 rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-rag-red disabled:opacity-50"
                aria-label="Remove attachment"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
        <input
          className={FIELD_INPUT_CLS}
          placeholder="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={disabled}
        />
        <input
          className={FIELD_INPUT_CLS}
          placeholder="https://drive.google.com/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={disabled}
        />
        <button
          type="button"
          disabled={disabled || url.trim().length === 0}
          onClick={() => void add()}
          className="inline-flex items-center justify-center gap-1 rounded-md bg-tcs-inverse px-3 py-2 text-xs font-medium text-tcs-inverse-ink disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Tab 5: Attachments — read-only Trello carry-over links. Storage uploads
// are out of scope for this task per the spec.
// ----------------------------------------------------------------------------

function AttachmentsTab({
  row,
  onPatch,
  disabled,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
}) {
  const attachments = Array.isArray(row.attachments) ? row.attachments : [];
  const noLinks = attachments.length === 0;

  return (
    <div className="space-y-4">
      {/* Editable document links (#9 Documents section) — Drive URLs for the
          sub/insurance/employment/building-service doc set. */}
      <section className="grid gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
          Document links
        </h3>
        <DebouncedTextField
          label="Signed proposal (Drive URL)"
          value={row.signed_proposal_url ?? ''}
          onSave={(v) => onPatch({ signed_proposal_url: v || null })}
          disabled={disabled}
        />
        <DebouncedTextField
          label="Insurance (COI) doc"
          value={row.insurance_doc_link ?? ''}
          onSave={(v) => onPatch({ insurance_doc_link: v || null })}
          disabled={disabled}
        />
        <DebouncedTextField
          label="Employment / sub agreement"
          value={row.employment_agreement_link ?? ''}
          onSave={(v) => onPatch({ employment_agreement_link: v || null })}
          disabled={disabled}
        />
        <DebouncedTextField
          label="Building service agreement"
          value={row.building_service_agreement_link ?? ''}
          onSave={(v) => onPatch({ building_service_agreement_link: v || null })}
          disabled={disabled}
        />
      </section>

      <OtherAttachments row={row} onPatch={onPatch} disabled={disabled} />

      {noLinks && (
        <p className="rounded-md border border-dashed border-tcs-border-strong p-4 text-xs text-tcs-text-muted">
          No file attachments yet. File upload lands in a follow-up task; for
          now use the document links above or the Notes field.
        </p>
      )}
      {/* The "Imported from Trello" link list and the "View original Trello card"
          link used to render here, off row.trello_attachment_links and
          row.original_trello_url. Those columns are dropped (2026-08-26): they were
          the last two of the nine Trello columns anybody could see, they existed on
          6 of 38 cards, and both values now sit in the card's own Notes, which
          survives the drop and is where a person already looks for card history. */}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab 6: Activity — audit_log entries for this sop_id, newest-first.
// audit_log RLS already gates visibility per role.
// ----------------------------------------------------------------------------

// Real audit_log shape (verified 2026-05-29): record_id (not row_pk),
// changed_fields (not changed_columns), changed_by_employee_id (not actor_eid),
// changed_at (not created_at). The prior column names silently errored, so the
// Activity tab always showed "not available". (#9 edit history.)
interface AuditEntry {
  audit_id: string | number;
  action: string;
  changed_fields: string[] | null;
  changed_by_employee_id: string | null;
  changed_by_role: string | null;
  changed_at: string;
}

function ActivityTab({ sopId }: { sopId: string }) {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('audit_log')
          .select('audit_id, action, changed_fields, changed_by_employee_id, changed_by_role, changed_at')
          .eq('table_name', 'new_account_sop')
          .eq('record_id', sopId)
          .order('changed_at', { ascending: false })
          .limit(100);
        if (cancelled) return;
        if (error) {
          setErr(error.message);
          setRows([]);
          return;
        }
        const entries = (data ?? []) as unknown as AuditEntry[];
        setRows(entries);
        // Resolve actor EIDs → human names (memory: lead with human names).
        const eids = Array.from(
          new Set(entries.map((e) => e.changed_by_employee_id).filter(Boolean) as string[]),
        );
        if (eids.length > 0) {
          const { data: emps } = await supabase
            .from('v_employees_am')
            .select('employee_id, employee_name_first, employee_name_last')
            .in('employee_id', eids);
          if (!cancelled && emps) {
            const map: Record<string, string> = {};
            for (const e of emps) {
              map[e.employee_id] =
                [e.employee_name_first, e.employee_name_last].filter(Boolean).join(' ').trim() ||
                e.employee_id;
            }
            setNames(map);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e, 'Could not load activity.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sopId]);

  // Group newest-first entries by actor (changed_by_employee_id) while keeping
  // chronological order within each actor's most-recent burst.
  const groups = useMemo(() => {
    const out: { actor: string; label: string; entries: AuditEntry[] }[] = [];
    for (const r of rows) {
      const actor = r.changed_by_employee_id ?? 'system';
      const last = out[out.length - 1];
      if (last && last.actor === actor) {
        last.entries.push(r);
      } else {
        out.push({
          actor,
          label: r.changed_by_employee_id ? names[r.changed_by_employee_id] ?? r.changed_by_employee_id : 'System',
          entries: [r],
        });
      }
    }
    return out;
  }, [rows, names]);

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-xs text-tcs-text-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading activity…
      </p>
    );
  }
  if (err) {
    return (
      <p className="rounded-md border border-rag-amber bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
        Activity log not available: {err}
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-tcs-border-strong p-4 text-xs text-tcs-text-muted">
        No activity yet.
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {groups.map((g, gi) => (
        <li key={`${g.actor}-${gi}`} className="rounded-md border border-tcs-border bg-tcs-raised">
          <div className="border-b border-tcs-border bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text-secondary">
            {g.label}
          </div>
          <ul className="divide-y divide-tcs-border">
            {g.entries.map((r) => (
              <li key={r.audit_id} className="px-3 py-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-tcs-text-secondary">{r.action}</span>
                  <span className="text-tcs-text-muted">{new Date(r.changed_at).toLocaleString()}</span>
                </div>
                {r.changed_fields && r.changed_fields.length > 0 && (
                  <div className="mt-1 text-[11px] text-tcs-text-muted">
                    Fields: {r.changed_fields.join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

// ----------------------------------------------------------------------------
// Footer — stage / cancel / stall actions. Keeps users out of the more
// destructive paths unless they intend them (confirm prompts).
// ----------------------------------------------------------------------------

// True when every checklist item in the cluster(s) mapped to `dbStage` is
// checked. Used as the stage-advance gate (#9) — the checklist is the gate the
// UI actually maintains, so we enforce "can't advance until it's complete".
function Footer({
  row,
  onPatch,
  disabled,
  onDelete,
}: {
  row: NewAccountSop;
  onPatch: (p: SopPatch) => Promise<void>;
  disabled: boolean;
  // Provided only when the viewer may delete this card (owner or admin).
  // Receives the required deletion reason (snapshotted to deletion_requests).
  onDelete?: (reason: string) => Promise<void>;
}) {
  const stages = NEW_ACCOUNT_SOP_STAGES;
  const [gateError, setGateError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setGateError(null);
    if (!onDelete) return;
    // What happens to the building it made (Ben 2026-10-26, option C), said
    // before the reason is asked for — the same answer the delete acts on.
    let buildingWords: string | null = null;
    try {
      buildingWords = deleteBuildingWords(await previewSopDelete(row.sop_id));
    } catch (e) {
      setGateError(errorMessage(e, 'Could not check what deleting this card does.'));
      return;
    }
    const reason = window.prompt(
      'Delete this SOP card? It is removed from the board (a full snapshot is ' +
        'written to the deletion log first, so it can be recovered). Use this ' +
        'only for cards created in error — it is separate from "Cancel card".' +
        (buildingWords ? `\n\n${buildingWords}` : '') +
        '\n\nReason for deletion:',
      '',
    );
    if (reason === null) return; // user cancelled the prompt
    if (reason.trim() === '') {
      setGateError('A reason is required to delete a card.');
      return;
    }
    setDeleting(true);
    try {
      await onDelete(reason.trim());
    } catch (e) {
      setDeleting(false);
      setGateError(errorMessage(e, 'Could not delete the card.'));
    }
  }

  function attemptMove(next: NewAccountSopStage) {
    setGateError(null);
    if (!next || next === row.stage) return;

    // The screen's rule lives in lib/stage-move.ts (AFB-0081 / W-03 hard gate,
    // one forward stage at a time, the soft checklist gate of 2026-06-12) so
    // Ask AI's card asks the same question the same way.
    const { blocked, warn } = stageMoveCheck(row, next);
    if (blocked) {
      setGateError(blocked);
      return;
    }
    if (warn && !window.confirm(warn)) return;
    void onPatch({ stage: next });
  }

  return (
    <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-tcs-border bg-tcs-bg px-4 py-3 text-xs">
      {onDelete && (
        <button
          type="button"
          disabled={disabled || deleting}
          onClick={() => void handleDelete()}
          className="mr-auto inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1.5 font-medium text-tcs-text-secondary hover:border-rag-red hover:text-rag-red disabled:opacity-50"
          title="Delete this card (created in error)"
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          Delete
        </button>
      )}
      {gateError && (
        <span className="mr-auto max-w-[60%] rounded-md border border-rag-amber bg-st-warn-bg px-2 py-1 text-[11px] text-st-warn-fg-strong">
          {gateError}
        </span>
      )}
      <select
        value={row.stage ?? ''}
        disabled={disabled}
        onChange={(e) => attemptMove(e.target.value as NewAccountSopStage)}
        className="rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5"
        aria-label="Move to stage"
      >
        {stages.map((s) => (
          <option key={s} value={s}>
            Move to {NEW_ACCOUNT_SOP_STAGE_LABELS[s]}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={disabled || row.status === 'Stalled'}
        onClick={() => onPatch({ status: 'Stalled' })}
        className="rounded-md border border-rag-amber bg-st-warn-bg px-2.5 py-1.5 font-medium text-st-warn-fg-strong disabled:opacity-50"
      >
        Mark stalled
      </button>
      <button
        type="button"
        disabled={disabled || row.stage === 'Cancelled'}
        onClick={() => {
          const reason = window.prompt(
            'Why is this SOP being cancelled? (visible on the card)',
            row.cancelled_reason ?? '',
          );
          if (reason === null) return;
          void onPatch({
            stage: 'Cancelled',
            status: 'Cancelled',
            // The person's own date, not UTC's (an evening cancel was dated tomorrow).
            cancelled_date: localToday(),
            cancelled_reason: reason || null,
          });
        }}
        className="rounded-md border border-rag-red bg-st-danger-bg px-2.5 py-1.5 font-medium text-rag-red disabled:opacity-50"
      >
        Cancel card
      </button>
    </footer>
  );
}

// ----------------------------------------------------------------------------
// Small editable building blocks.
// ----------------------------------------------------------------------------

const FIELD_INPUT_CLS =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

function FieldShell({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-tcs-text">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-tcs-text-secondary">{hint}</span>}
    </label>
  );
}

function DebouncedTextField({
  label,
  value,
  onSave,
  disabled,
  type = 'text',
  hint,
}: {
  label: string;
  value: string;
  onSave: (v: string) => Promise<void> | void;
  disabled?: boolean;
  type?: 'text' | 'email' | 'tel' | 'date';
  /** One quiet line under the box — say WHERE the value is stored when that is
   *  not where the reader would assume. */
  hint?: string;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <FieldShell label={label}>
      <input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const trimmed = v.trim();
          if (trimmed === (value ?? '').trim()) return;
          void onSave(trimmed);
        }}
        disabled={disabled}
        className={FIELD_INPUT_CLS}
        autoComplete="off"
      />
      {hint && <p className="mt-1 text-[11px] text-tcs-text-muted">{hint}</p>}
    </FieldShell>
  );
}

function DebouncedTextArea({
  label,
  value,
  onSave,
  disabled,
}: {
  label: string;
  value: string;
  onSave: (v: string) => Promise<void> | void;
  disabled?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <FieldShell label={label}>
      <textarea
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const trimmed = v.trim();
          if (trimmed === (value ?? '').trim()) return;
          void onSave(trimmed);
        }}
        disabled={disabled}
        rows={4}
        className={`${FIELD_INPUT_CLS} min-h-[96px]`}
      />
    </FieldShell>
  );
}

function NumberField({
  label,
  value,
  onSave,
  disabled,
  hint,
}: {
  label: string;
  value: number | null | undefined;
  onSave: (v: number | null) => Promise<void> | void;
  disabled?: boolean;
  hint?: string;
}) {
  const [v, setV] = useState<string>(value == null ? '' : String(value));
  useEffect(() => setV(value == null ? '' : String(value)), [value]);
  return (
    <FieldShell label={label} hint={hint}>
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const trimmed = v.trim();
          if (trimmed === '') {
            if (value != null) void onSave(null);
            return;
          }
          const n = Number(trimmed);
          if (Number.isNaN(n)) return;
          if (n === value) return;
          void onSave(n);
        }}
        disabled={disabled}
        className={FIELD_INPUT_CLS}
      />
    </FieldShell>
  );
}

/**
 * AFB-0081 — the management fee, in the units a person thinks in.
 *
 * The column is a FRACTION (0.0300) and NumberField would show "0.03", so this
 * reads and writes percent while persisting the fraction, via the same
 * ./mgmt-fee helpers the intake modal uses. Three states, kept distinct:
 *
 *   NULL  -> empty, placeholder "Not answered"   (the legacy gap)
 *   0     -> "0"                                  (deliberately no fee)
 *   0.03  -> "3"
 *
 * Clearing the field records 0, NOT NULL. Intake never writes NULL — the toggle
 * off persists an explicit 0 — so an editor that could re-create NULL would let
 * somebody silently un-answer a card and trip the stage gate with no way to see
 * why. "Unanswered" is a state this app inherited, not one it should be able to
 * produce.
 */
function FeePercentField({
  value,
  onSave,
  disabled,
}: {
  value: number | null | undefined;
  onSave: (v: number) => Promise<void> | void;
  disabled?: boolean;
}) {
  const asPct = (v: number | null | undefined) =>
    v == null ? '' : v === 0 ? '0' : fractionToFeePct(v);
  const [v, setV] = useState<string>(asPct(value));
  useEffect(() => setV(asPct(value)), [value]);
  return (
    <FieldShell
      label="Management fee (%)"
      hint="Clear the field to record 0% — no management fee. Blank means nobody has answered yet, which only happens on cards created before July 2026."
    >
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={v}
        placeholder={value == null ? 'Not answered' : ''}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const next = v.trim() === '' ? 0 : feePctToFraction(v);
          if (next == null) {
            setV(asPct(value)); // unparseable — put the stored value back
            return;
          }
          if (next === value) return;
          void onSave(next);
        }}
        disabled={disabled}
        className={FIELD_INPUT_CLS}
      />
    </FieldShell>
  );
}

function SelectField({
  label,
  value,
  options,
  onSave,
  disabled,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onSave: (v: string) => Promise<void> | void;
  disabled?: boolean;
}) {
  return (
    <FieldShell label={label}>
      <select
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          if (next === value) return;
          void onSave(next);
        }}
        disabled={disabled}
        className={FIELD_INPUT_CLS}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

// ----------------------------------------------------------------------------
// #5 — "Generate summary": compile the card into a copyable handoff email that
// Antonela forwards to Amy for Gusto / billing setup. Client-side text only —
// no mail API (Phase 1). Reads straight off the row, so every field the user
// entered across the tabs is included.
// ----------------------------------------------------------------------------

function fmtSummaryMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}

function orDashStr(v: string | null | undefined): string {
  const t = (v ?? '').toString().trim();
  return t.length > 0 ? t : '—';
}

function buildAccountSummaryEmail(row: NewAccountSop): {
  subject: string;
  body: string;
} {
  const facilityType = row.facility_type
    ? (NAS_FACILITY_TYPE_LABELS as Record<string, string>)[row.facility_type] ??
      String(row.facility_type)
    : '—';
  const facilityLine = row.facility_name?.trim()
    ? `${row.facility_name.trim()} (${facilityType})`
    : facilityType;
  const address =
    [
      row.client_address_street,
      [row.client_address_city, row.client_address_state]
        .map((s) => (s ?? '').toString().trim())
        .filter((s) => s.length > 0)
        .join(', '),
      row.client_address_zip,
    ]
      .map((s) => (s ?? '').toString().trim())
      .filter((s) => s.length > 0)
      .join(' · ') || '—';
  const billingContact = [
    orDashStr(row.billing_contact_name),
    orDashStr(row.billing_contact_email),
    orDashStr(row.billing_contact_phone),
  ].join(' · ');
  const subId = row.service_provider_id?.trim();
  const account = cardDisplayName(row);
  const subject = `New account setup — ${account}`;
  // Items 831/758: an hourly card's money is said per hour in the email too.
  const hourly = hourlyCardRates(row.service_lines);
  const subPayLine = hourly
    ? `- Contract rate: ${hourly.sub != null ? perHour(hourly.sub) : '—'} (paid on actual hours, no monthly figure)`
    : `- Monthly sub payment: ${fmtSummaryMoney(row.sub_payment)}`;
  const clientPriceLine = hourly
    ? `- Client rate: ${hourlyBilledText(hourly.client, { cents: true })}`
    : `- Client monthly price: ${fmtSummaryMoney(row.final_pricing)}`;

  const body = [
    `Hi Finance,`,
    ``,
    `We have a new account ready for Gusto / billing setup. Details below:`,
    ``,
    `ACCOUNT`,
    `- Account: ${orDashStr(row.account_name)}`,
    `- Facility: ${facilityLine}`,
    `- Market: ${orDashStr(row.market_region)}`,
    `- Start date: ${orDashStr(row.start_date)}`,
    ``,
    `SUBCONTRACTOR`,
    `- Name: ${orDashStr(row.contractor_name)}`,
    `- Provider ID: ${subId ? subId : '— (not yet linked)'}`,
    subPayLine,
    `- Scope Performance Payment: ${fmtSummaryMoney(row.no_miss_incentive)}`,
    `- Service days / week: ${row.service_days_per_week ?? '—'}`,
    ``,
    `CLIENT BILLING`,
    clientPriceLine,
    // AFB-0081. This email IS the billing-setup handoff to Amy, and it left out
    // the two things that change what gets billed. "Not answered" is printed
    // rather than omitted: a missing line reads as "no fee" / "no usables" to
    // whoever sets the account up, which is the same wrong answer the UI was
    // giving.
    `- Management fee: ${
      row.management_fee_pct == null
        ? 'NOT ANSWERED — confirm before invoicing'
        : row.management_fee_pct === 0
          ? 'none (0%)'
          : `${fractionToFeePct(row.management_fee_pct)}%`
    }`,
    `- Usables: ${
      row.tcs_provides_usables == null
        ? 'NOT ANSWERED — confirm before invoicing'
        : usablesLabel(row.tcs_provides_usables)
    }`,
    `- Payment terms: ${orDashStr(row.payment_terms)}`,
    `- Payment frequency: ${orDashStr(row.payment_frequency)}`,
    `- Billing contact: ${billingContact}`,
    `- Client address: ${address}`,
    ``,
    `Thanks,`,
    `HR`,
  ].join('\n');

  return { subject, body };
}

function SummaryModal({
  row,
  onClose,
}: {
  row: NewAccountSop;
  onClose: () => void;
}) {
  const { subject, body } = useMemo(() => buildAccountSummaryEmail(row), [row]);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-xl bg-tcs-overlay shadow-xl sm:max-h-[88vh] sm:w-[600px] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Account summary email"
      >
        <header className="flex items-start justify-between gap-3 border-b border-tcs-border bg-tcs-bg p-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-tcs-text">Account summary</h2>
            <p className="mt-0.5 text-xs text-tcs-text-secondary">
              Copy this into an email to Finance for Gusto / billing setup.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border bg-tcs-raised p-1.5 text-tcs-text-secondary hover:bg-tcs-bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <p className="mb-1 text-xs font-medium text-tcs-text">Subject</p>
          <p className="mb-3 rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text">
            {subject}
          </p>
          <p className="mb-1 text-xs font-medium text-tcs-text">Body</p>
          <textarea
            readOnly
            value={body}
            rows={18}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 font-mono text-xs leading-relaxed text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-tcs-border bg-tcs-bg px-4 py-3">
          <a
            href={mailto}
            className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-bg-muted"
          >
            Open in email
          </a>
          <button
            type="button"
            onClick={() => void copy()}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied!' : 'Copy email'}
          </button>
        </footer>
      </div>
    </div>
  );
}
